/*
 * The client portal, for a foreign national signed in to their own record.
 * It has its own shell (firm branding, bottom tabs on phones) and none of the
 * attorney app's providers.
 *
 *   /portal                Home: what we need from you, your case, your team
 *   /portal/case/:caseId   One case: stage, what happens next, receipts, updates
 *   /portal/tasks          Everything waiting on the client
 *   /portal/documents      Their uploads and what the firm shared
 *   /portal/profile        Their details, with suggested corrections
 *   /portal/sign/:id       Sign a document on screen, or in ink for USCIS
 *   /portal/sign-in        Ask for a sign-in link by email
 *   /portal/welcome        Where an emailed link lands (?token=)
 */
export const CLIENT_PORTAL_PATH = "/portal";
export const CLIENT_PORTAL_CASE_PATH = "/portal/case";
export const CLIENT_PORTAL_TASKS_PATH = "/portal/tasks";
export const CLIENT_PORTAL_DOCUMENTS_PATH = "/portal/documents";
export const CLIENT_PORTAL_PROFILE_PATH = "/portal/profile";
export const CLIENT_PORTAL_SIGN_PATH = "/portal/sign";
export const CLIENT_PORTAL_SIGN_IN_PATH = "/portal/sign-in";
export const CLIENT_PORTAL_WELCOME_PATH = "/portal/welcome";

export const clientPortalCasePath = (caseId: string): string =>
  `${CLIENT_PORTAL_CASE_PATH}/${encodeURIComponent(caseId)}`;

export const clientPortalSignPath = (requestId: string): string =>
  `${CLIENT_PORTAL_SIGN_PATH}/${encodeURIComponent(requestId)}`;

const RETURN_KEY = "clientPortal.returnTo";
const RETURN_TTL_MS = 60 * 60 * 1000;

const isReturnable = (path: string): boolean =>
  path.startsWith(`${CLIENT_PORTAL_PATH}/`) &&
  !path.startsWith(CLIENT_PORTAL_SIGN_IN_PATH) &&
  !path.startsWith(CLIENT_PORTAL_WELCOME_PATH);

/** A signed-out deep link (e.g. "sign this" from an email) survives the
 * sign-in email, which usually opens in a new tab. */
export const rememberPortalReturn = (path: string, now = Date.now()): void => {
  if (!isReturnable(path)) return;
  localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: now }));
};

/** Where to land after signing in; reads once. */
export const takePortalReturn = (now = Date.now()): string => {
  const raw = localStorage.getItem(RETURN_KEY);
  localStorage.removeItem(RETURN_KEY);
  try {
    const saved = JSON.parse(raw ?? "null") as { path?: unknown; at?: unknown };
    if (
      typeof saved?.path === "string" &&
      typeof saved.at === "number" &&
      now - saved.at < RETURN_TTL_MS &&
      isReturnable(saved.path)
    )
      return saved.path;
  } catch {
    // A corrupt entry just means home.
  }
  return CLIENT_PORTAL_PATH;
};

/** The retired `/client?tab=` portal, mapped onto the new pages. */
export const legacyClientPortalPath = (search: string): string => {
  const tab = new URLSearchParams(search).get("tab");
  if (tab === "documents") return CLIENT_PORTAL_DOCUMENTS_PATH;
  if (tab === "outgoing_intakes") return CLIENT_PORTAL_TASKS_PATH;
  return CLIENT_PORTAL_PATH;
};
